/**
 * 人员选择器 — 通用 Select 组件（多选/单选/只读）
 * value 统一为 UserSelectUser 对象（多选 UserSelectUser[]，单选 UserSelectUser|null）
 * #9510
 */
import React, { useState, useMemo, useEffect } from 'react';
import { Select } from '@bedrock/components';
import POPOUserList from '@mpc/common/src/features/POPOUserList';
import { v2RubyMigrateUsersSearchActiveUsers } from 'services/api';
import { debounce } from 'lodash';

export interface UserSelectUser {
  id: number;
  name: string;
  email: string;
}

export interface UserSelectProps {
  multiple?: boolean;
  value?: UserSelectUser[] | UserSelectUser | null;
  onChange?: (val: UserSelectUser[] | UserSelectUser | null) => void;
  searchLimit?: number;
  maxCount?: number;
  prefillUsers?: UserSelectUser[];
  view?: boolean;
  disabled?: boolean;
  allowClear?: boolean;
}

const UserSelect: React.FC<UserSelectProps> = ({
  value,
  onChange,
  multiple = true,
  searchLimit = 30,
  maxCount,
  prefillUsers,
  view = false,
  disabled = false,
  allowClear = true,
}) => {
  const [userOptions, setUserOptions] = useState<
    { name: string; value: number; email: string }[]
  >([]);
  const [userSearching, setUserSearching] = useState(false);
  const [userSearchKey, setUserSearchKey] = useState('');
  const userCacheRef = React.useRef<Map<number, UserSelectUser>>(new Map());

  // 预填
  useEffect(() => {
    if (prefillUsers?.length) {
      const map = new Map<number, UserSelectUser>();
      const opts = prefillUsers.map((u) => {
        map.set(u.id, u);
        return { name: `${u.name}(${u.email})`, value: u.id, email: u.email };
      });
      setUserOptions(opts);
      userCacheRef.current = map;
    } else {
      setUserOptions([]);
      userCacheRef.current = new Map();
    }
  }, [prefillUsers]);

  // 搜索
  const handleSearch = useMemo(
    () =>
      debounce(async (key: string) => {
        const k = key?.trim();
        if (!k) {
          setUserSearchKey('');
          if (!prefillUsers?.length) setUserOptions([]);
          return;
        }
        setUserSearchKey(k);
        setUserSearching(true);
        try {
          const res = await v2RubyMigrateUsersSearchActiveUsers({
            search_text: k,
            limit: searchLimit,
          });
          if (res.res_code === 1 && res.data) {
            // 按 email 去重（API 可能返回同一邮箱的多条重复记录）
            const seen = new Set<string>();
            const opts = [];
            for (const u of res.data) {
              const email = (u.email ?? '').toLowerCase();
              if (seen.has(email)) continue;
              seen.add(email);
              const user: UserSelectUser = {
                id: u.id as number,
                name: u.name,
                email: u.email ?? '',
              };
              userCacheRef.current.set(user.id, user);
              opts.push({
                name: `${u.name}(${u.email})`,
                value: user.id,
                email: u.email ?? '',
              });
            }
            setUserOptions(opts);
          } else {
            setUserOptions([]);
          }
        } finally {
          setUserSearching(false);
        }
      }, 300),
    [searchLimit, prefillUsers?.length]
  );

  const lookup = (id: number): UserSelectUser =>
    userCacheRef.current.get(id) ??
    (() => {
      const o = userOptions.find((x) => x.value === id);
      return { id, name: o?.name?.split('(')[0] || '', email: o?.email || '' };
    })();

  const selectValue = multiple
    ? ((value as UserSelectUser[]) ?? []).map((u) => u.id)
    : ((value as UserSelectUser | null)?.id ?? undefined);

  const handleMulti = (ids: number[]) => {
    if (maxCount && maxCount > 0 && ids.length > maxCount) return;
    (onChange as (v: UserSelectUser[]) => void)?.(ids.map(lookup));
  };
  const handleSingle = (id: number | null) => {
    (onChange as (v: UserSelectUser | null) => void)?.(
      id != null ? lookup(id) : null
    );
  };

  // 只读态提前返回：必须置于全部 Hook 之后。view 是调用方可动态翻转的 prop
  // （如 AssessorSelect 传 view={!editable}，提交报告后 permit_actions 变化即翻转），
  // 若在 Hook 之前 return，翻转前后两次渲染的 Hook 数量不一致 → Rules of Hooks 报错。
  if (view) {
    return <POPOUserList list={(value as UserSelectUser[]) || []} />;
  }

  return (
    <Select
      multiple={multiple}
      value={selectValue}
      onChange={multiple ? handleMulti : handleSingle}
      options={userOptions}
      type="label"
      showSearch
      filterOption={false}
      allowClear={allowClear}
      disabled={disabled}
      virtual
      notFoundContent={
        userSearching
          ? '搜索中...'
          : userSearchKey
            ? '暂无数据'
            : '请输入搜索关键字'
      }
      onSearch={handleSearch}
      onClear={() => setUserOptions([])}
    />
  );
};

export default UserSelect;
